// Google Analytics 4 (gtag.js), carregado por JS e não por <script> no index.html.
//
// POR QUE NÃO A TAG ESTÁTICA NO index.html:
// dois motivos, e os dois são consequência do que este site é.
//
// 1. Consentimento. O snippet do index.html baixa o gtag e grava cookie no primeiro byte da
//    página, antes de o visitante ver o banner. Aqui a medição só começa depois do aceite —
//    quem nunca clicou não gera requisição alguma para o googletagmanager.com.
// 2. SPA. `gtag('config', ID)` conta UMA visualização, a do carregamento inicial. Como a
//    navegação daqui em diante é do React Router, sem recarregar documento, todo o resto do
//    site ficaria invisível no relatório: a Home somaria tudo e as telas internas ficariam
//    zeradas. Por isso o config vai com `send_page_view: false` e cada rota é registrada à mão
//    por registrarPageview (ver o componente Analytics em App.jsx).
//
// MICROSOFT CLARITY (gravação de sessão e mapas de calor) segue as mesmas regras e entra pelo
// mesmo consentimento -- não existe um segundo "aceite" só para ele. Duas diferenças:
//
//  - Só no SITE. O app Android é o mesmo bundle, e lá o Clarity não roda (ver iniciarClarity).
//  - Revogar é `clarity('stop')`, e não `Clarity.consent(false)`. O consent(false) do Clarity
//    limpa os cookies e REINICIA a gravação em modo sem cookie -- continua gravando. O que
//    para de verdade é o `stop` do clarity-js, que o wrapper npm não expõe mas a função global
//    `window.clarity` despacha.
//
// Pageview o Clarity não precisa: ele observa o history do navegador sozinho.
//
// O /admin NÃO ENTRA NA CONTA, nem no GA nem no Clarity (ver ehRotaDaAdministracao). Só a rota, e não
// quem navega: as outras telas que o administrador abre continuam medidas. A v1.37.0 marcava o
// navegador do admin e o desligava inteiro, e isso fazia telas comuns sumirem do relatório sem deixar
// rastro dentro do GA. Filtro de IP no próprio GA também não serve aqui: com o CGNAT das operadoras,
// o IP do admin é o de muitos clientes da região, e filtro ativo descarta o dado para sempre. Ver
// prompts/medicao-so-sem-o-admin.md.

import Clarity from '@microsoft/clarity';
import { ehNativo, plataforma } from './plataforma';

const MEASUREMENT_ID = 'G-8P61Y79PSG';
const CLARITY_PROJECT_ID = 'ym0by8487q';

/** Chave no localStorage com a decisão sobre medição. */
export const CHAVE_CONSENTIMENTO = 'cookieConsent';

/**
 * Evento de janela emitido quando a pessoa DECIDE -- aceitando ou recusando.
 *
 * É o que faz a medição começar (ou parar) na mesma visita, sem esperar uma recarga. Um evento de
 * window, e não uma prop, porque quem pergunta e quem mede ficam em pontos distantes da árvore e
 * nada mais os relaciona.
 *
 * Quem escuta deve reler a decisão do armazenamento em vez de assumir "aceitou": desde que existe
 * recusa, este evento significa "mudou", não "sim".
 */
export const EVENTO_CONSENTIMENTO = 'novafaxina:consentimento-cookies';

/**
 * São TRÊS estados, e a diferença entre dois deles é o que a recusa introduziu.
 *
 *  - ACEITO: mede.
 *  - RECUSADO: não mede, e **não pergunta de novo**. É o que separa recusar de ignorar.
 *  - null (nunca decidiu): não mede, e pergunta.
 *
 * Antes existiam só "aceitou" e "não aceitou", e por isso quem não quisesse ser medido convivia
 * com a faixa para sempre -- a única forma de fazê-la sumir era aceitar.
 *
 * No app Android o null não existe: quem nunca decidiu é lido como ACEITO (ver
 * decisaoDeConsentimento). Lá a medição é opt-out, e não opt-in como no site.
 */
export const CONSENTIMENTO = { ACEITO: 'true', RECUSADO: 'false' };

let iniciado = false;

/**
 * O Clarity já foi carregado e depois parado por uma revogação. Precisa de flag própria porque
 * religar não é o mesmo que ligar: `Clarity.init` é no-op quando o <script> já está na página
 * (ele checa o id `clarity-script`), então o retorno se faz por `clarity('start')`.
 */
let clarityParado = false;

/**
 * O Clarity foi parado por estar numa tela do /admin, e volta ao sair dela. É separada de
 * clarityParado de propósito: sair do /admin não pode religar quem revogou o consentimento.
 */
let clarityPausadoNoAdmin = false;

/**
 * localStorage lança em vez de devolver null quando o navegador bloqueia armazenamento de site
 * (modo privado de alguns navegadores, política corporativa). Sem o try o erro subiria no meio
 * do render e derrubaria a página inteira — por causa da medição, que é o que menos importa
 * aqui. Falhou ao ler: trata como "não consentiu".
 */
export const temConsentimento = () => decisaoDeConsentimento() === CONSENTIMENTO.ACEITO;

/**
 * A decisão guardada, ou null se nunca houve uma.
 *
 * É a leitura que decide se a pergunta aparece. `temConsentimento()` acima não serve para isso:
 * ela devolve false tanto para quem recusou quanto para quem nunca viu a pergunta, e usá-la para
 * decidir se pergunta faria a faixa voltar a aparecer para quem já disse não.
 *
 * NO APP ANDROID, "nunca decidiu" vale como aceite. A tela de pergunta da abertura
 * (ConsentimentoApp) foi retirada lá: o que o app mede é só quais telas são abertas, sem
 * Clarity, e isso passou a ser coberto por legítimo interesse, com opt-out em Meu cadastro ›
 * Minha Conta. Uma recusa gravada continua valendo, e é ela que sustenta esse opt-out.
 *
 * O padrão é calculado na leitura e não gravado no armazenamento: assim "aceitou" e "nunca disse
 * nada" continuam distinguíveis se a regra mudar. Como ConsentimentoApp só abre diante de null,
 * no Android ela some sem precisar de um `if` próprio.
 */
export const decisaoDeConsentimento = () => {
    const padrao = plataforma() === 'android' ? CONSENTIMENTO.ACEITO : null;
    try {
        const guardado = localStorage.getItem(CHAVE_CONSENTIMENTO);
        return guardado === CONSENTIMENTO.ACEITO || guardado === CONSENTIMENTO.RECUSADO
            ? guardado
            : padrao;
    } catch (error) {
        // Armazenamento bloqueado: trata como "nunca decidiu". No site, perguntar de novo é melhor
        // do que medir alguém que talvez tenha recusado. No Android vale o padrão opt-out.
        return padrao;
    }
};

/**
 * Grava a decisão e a aplica AGORA, sem esperar recarga.
 *
 * @param {boolean} aceitou
 */
export const registrarConsentimento = (aceitou) => {
    try {
        localStorage.setItem(
            CHAVE_CONSENTIMENTO,
            aceitou ? CONSENTIMENTO.ACEITO : CONSENTIMENTO.RECUSADO,
        );
    } catch (error) {
        // Sem armazenamento a escolha vale só nesta sessão. Aplicar mesmo assim é o certo: a
        // pessoa clicou, e ignorar o clique porque o navegador não deixa gravar seria pior.
    }

    if (!aceitou) pararDeMedir();

    window.dispatchEvent(new Event(EVENTO_CONSENTIMENTO));
};

/**
 * Desliga a medição de quem já estava sendo medido e mudou de ideia.
 *
 * Isto não é simétrico a ligar, e é por isso que precisa de código próprio: o gtag.js, uma vez
 * injetado, não sai da página. O que existe é a chave `ga-disable-<ID>` que o próprio Google
 * documenta para este caso -- com ela em true, o script continua carregado e para de enviar.
 *
 * `iniciado` volta a false para que um aceite posterior reinicie o fluxo normalmente.
 *
 * O Clarity para pelo `stop` (ver o cabeçalho). Se `window.clarity` ainda for o stub de fila --
 * o script foi injetado e não terminou de baixar -- o `stop` fica enfileirado e é aplicado
 * assim que carregar, que é exatamente o que se quer.
 */
const pararDeMedir = () => {
    window[`ga-disable-${MEASUREMENT_ID}`] = true;
    iniciado = false;

    if (typeof window.clarity === 'function') {
        window.clarity('stop');
        clarityParado = true;
    }
    clarityPausadoNoAdmin = false;
};

/** `/admin` e tudo abaixo dele. `caminho` pode trazer querystring (ver registrarPageview). */
export const ehRotaDaAdministracao = (caminho) => /^\/admin(?:[/?#]|$)/.test(caminho || '');

/**
 * Pausa o Clarity ao entrar no /admin e o retoma ao sair. Se o script ainda estiver baixando,
 * `window.clarity` é o stub de fila e o comando é aplicado quando ele carregar (ver pararDeMedir).
 * No app não há Clarity, e o `typeof` cobre isso.
 */
const acompanharClarityNaRota = (noAdmin) => {
    if (typeof window.clarity !== 'function' || noAdmin === clarityPausadoNoAdmin) return;
    window.clarity(noAdmin ? 'stop' : 'start');
    clarityPausadoNoAdmin = noAdmin;
};

/**
 * Carrega o Clarity -- só no site. No app o bundle é o mesmo, mas a tela de consentimento de lá
 * (ConsentimentoApp) promete "quais telas são abertas" e nada mais; gravação de sessão não cabe
 * nessa promessa, e o app não tem a Política de Cookies do site para ampará-la.
 *
 * `consent(true)` logo após o init: é o que o Clarity pede quando o projeto está com "Require
 * cookie consent" ligado no painel, e é inócuo quando está desligado. Assim a configuração do
 * painel pode mudar sem mexer aqui.
 */
const iniciarClarity = () => {
    if (ehNativo()) return;

    if (clarityParado) {
        window.clarity('start');
        clarityParado = false;
        return;
    }

    Clarity.init(CLARITY_PROJECT_ID);
    Clarity.consent(true);
};

/**
 * Injeta o gtag e configura a propriedade; no site, carrega também o Clarity. Não envia
 * visualização: quem envia é registrarPageview, inclusive a primeira.
 *
 * Idempotente e silenciosa sem consentimento — pode ser chamada a cada mudança de rota sem
 * duplicar script nem medição.
 */
export const iniciarAnalytics = () => {
    if (iniciado || !temConsentimento()) return;
    iniciado = true;

    // Levanta o desligamento de uma recusa anterior. Sem isto, quem recusou e depois voltou atrás
    // teria o script rodando e mudo: `iniciado` viraria true, os pageviews sairiam, e a chave
    // `ga-disable` continuaria descartando todos eles em silêncio.
    window[`ga-disable-${MEASUREMENT_ID}`] = false;

    // As chamadas ficam na fila do dataLayer e são processadas quando o script terminar de
    // baixar, então a ordem entre configurar e injetar não importa.
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        window.dataLayer.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', MEASUREMENT_ID, { send_page_view: false });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(script);

    iniciarClarity();
};

/**
 * Uma visualização de tela. `caminho` é pathname + search — a querystring entra porque a busca
 * de profissionais guarda cidade, filtros e página nela, e sem isso todas as buscas colapsariam
 * numa linha só de /profissional no relatório.
 *
 * Rota do /admin não vira pageview, e o Clarity fica parado enquanto se estiver nela.
 */
export const registrarPageview = (caminho) => {
    // `temConsentimento()` de novo, e não só `iniciado`: entre o início da medição e esta chamada a
    // pessoa pode ter revogado nas configurações da conta. A guarda de cima, sozinha, continuaria
    // enviando até a próxima recarga.
    if (!iniciado || !temConsentimento() || typeof window.gtag !== 'function') return;

    const noAdmin = ehRotaDaAdministracao(caminho);
    acompanharClarityNaRota(noAdmin);
    if (noAdmin) return;

    window.gtag('event', 'page_view', {
        page_path: caminho,
        page_location: window.location.href,
        page_title: document.title,
    });
};
